import { useQuery } from '@tanstack/react-query';
import { FC, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { SrOnly } from '@/components/Layout/Parts';
import { SortableTh, Table, TableContent, Td, Th } from '@/components/Table';
import { EditorialQueuePage, SortState } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostNetwork, SortDirection } from '@/utils/enums';

import CopyButton from './CopyButton';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.ASC,
  property: 'name',
};

const StyledTable = styled(Table)`
  th,
  td {
    padding-top: 16px;
    padding-bottom: 16px;
    &:nth-child(1) {
      width: 40%;
      padding-left: 30px;
    }
    &:nth-child(2) {
      width: 30%;
    }
    &:nth-child(3) {
      width: 30%;
    }
  }
  td {
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
  }
`;

const PageHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Thumbnail = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  flex-shrink: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const BoardsButton = styled(Button)`
  border: 1px solid transparent;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purple} !important;
  }
`;

interface Props {
  network: EditorialFacebookPostNetwork;
  showPinterestBoards: (channelId: number) => void;
}

const PagesTable: FC<Props> = ({ network, showPinterestBoards }) => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const {
    data: pages,
    isLoading,
    isError,
  } = useQuery(
    ['editorial_distribution_queue_channels', { network, sort }],
    () =>
      api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels', {
        params: {
          ...(network === EditorialFacebookPostNetwork.INSTAGRAM && {
            'filter[hasInstagram]': true,
          }),
          ...(network === EditorialFacebookPostNetwork.THREADS && {
            'filter[hasThreads]': true,
          }),
          ...(network === EditorialFacebookPostNetwork.PINTEREST && {
            'filter[hasPinterest]': true,
          }),
          ...(sort.direction !== 'DEFAULT' && {
            sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
          }),
        },
      }),
    {
      staleTime: 120_000,
    },
  );

  return (
    <StyledTable $fixed>
      <thead>
        <tr>
          <SortableTh
            property="name"
            title="Page Name"
            setSort={setSort}
            sort={sort}
          />
          <SortableTh property="id" title="ID" setSort={setSort} sort={sort} />
          <Th>
            <SrOnly>Actions</SrOnly>
          </Th>
        </tr>
      </thead>
      <tbody>
        <TableContent
          isLoading={isLoading}
          hasData={!!pages?.length}
          isError={isError}
        >
          {pages?.map((page) => (
            <tr key={page.id}>
              <Td>
                <PageHolder>
                  {!!page.thumbnail && (
                    <Thumbnail src={page.thumbnail} alt={page.name} />
                  )}
                  {page.name}
                </PageHolder>
              </Td>
              <Td>{page.externalId}</Td>
              <Td>
                <Actions>
                  <CopyButton idToCopy={page.id} />
                  {network === EditorialFacebookPostNetwork.PINTEREST && (
                    <BoardsButton
                      color="colorized"
                      small
                      onClick={() => showPinterestBoards(page.id)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2 3.5H14V12C14 12.1326 13.9473 12.2598 13.8535 12.3536C13.7598 12.4473 13.6326 12.5 13.5 12.5H2.5C2.36739 12.5 2.24021 12.4473 2.14645 12.3536C2.05268 12.2598 2 12.1326 2 12V3.5Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 6.5H14"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 9.5H14"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.5 6.5V12.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Page Boards IDs
                    </BoardsButton>
                  )}
                </Actions>
              </Td>
            </tr>
          ))}
        </TableContent>
      </tbody>
    </StyledTable>
  );
};

export default PagesTable;
